import React, { useState, useEffect } from 'react';
import { GOOGLE_CLIENT_ID } from '../config/auth';
import OAuthDiagnostics, { getDiagnosticLogs } from './oauthDiagnostic';

/**
 * A component that can be added temporarily to diagnose OAuth issues
 */
const OAuthDebugger: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const info = {
    clientId: GOOGLE_CLIENT_ID,
    environment: process.env.NODE_ENV,
    origin: window.location.origin,
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    hash: window.location.hash,
    protocol: window.location.protocol,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  };
  
  // Log the information on mount
  useEffect(() => {
    console.log('[OAuth Debug]', info);
    
    // Add keyboard shortcut (Alt+D) to toggle debugger visibility
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'd') {
        setIsVisible(v => !v);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [info]);

  // Refresh logs every 3 seconds
  useEffect(() => {
    if (!isVisible) return;
    
    const refreshLogs = () => {
      setLogs(getDiagnosticLogs());
    };
    
    refreshLogs(); // Initial load
    const interval = setInterval(refreshLogs, 3000);
    return () => clearInterval(interval);
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const toggleLogs = () => {
    setIsVisible(!isVisible);
  };

  const clearLogs = () => {
    OAuthDiagnostics.clearDiagnosticLogs();
    setLogs([]);
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      zIndex: 9999,
      background: 'rgba(0, 0, 0, 0.8)',
      color: '#00ff00',
      fontFamily: 'monospace',
      padding: '15px',
      borderRadius: '5px',
      maxWidth: '80vw',
      maxHeight: '60vh',
      overflow: 'auto',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
    }}>
      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
        <strong>OAuth Debugger</strong>
        <button
          onClick={toggleLogs}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #00ff00',
            color: '#00ff00',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
        >
          Close
        </button>
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ margin: '5px 0', color: '#88ff88' }}>OAuth Configuration</h4>
        <pre style={{ margin: '0', fontSize: '12px' }}>
          {JSON.stringify(info, null, 2)}
        </pre>
      </div>
      
      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
        <h4 style={{ margin: '5px 0', color: '#88ff88' }}>Diagnostic Logs</h4>
        <button
          onClick={clearLogs}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #ff5555',
            color: '#ff5555',
            cursor: 'pointer',
            fontSize: '10px',
          }}
        >
          Clear Logs
        </button>
      </div>
      
      <div>
        {logs.length === 0 ? (
          <div style={{ color: '#aaaaaa', fontStyle: 'italic' }}>No logs yet...</div>
        ) : (
          <pre style={{ margin: '0', fontSize: '11px', maxHeight: '300px', overflow: 'auto' }}>
            {logs.map((log, index) => (
              <div key={index} style={{ marginBottom: '3px', wordBreak: 'break-all' }}>
                {log}
              </div>
            ))}
          </pre>
        )}
      </div>
    </div>
  );
};

export default OAuthDebugger; 